import { Timestamp } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import {
  CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRequest,
  CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherResponse,
  CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError,
  CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError_Reason,
  CreateHokkaidoR8HouseholderPostalReturnRowError,
  CreateHokkaidoR8HouseholderPostalReturnRowError_Reason,
  CreateHokkaidoR8HouseholderPostalReturnsRequest,
  CreateHokkaidoR8HouseholderPostalReturnsResponse,
  GetHokkaidoR8HouseholderPaperVouchersByPeriodRequest,
  GetHokkaidoR8HouseholderPaperVouchersByPeriodResponse,
  GetHokkaidoR8HouseholderPaperVouchersByPeriodResponse_Row,
  GetHokkaidoR8HouseholderPostalReturnsByPeriodRequest,
  GetHokkaidoR8HouseholderPostalReturnsByPeriodResponse,
  GetHokkaidoR8HouseholderPostalReturnsByPeriodResponse_Row,
  SearchHokkaidoR8HouseholderCodeStatusesRequest,
  SearchHokkaidoR8HouseholderCodeStatusesRequest_OrderBy as OrderBy,
  SearchHokkaidoR8HouseholderCodeStatusesRequest_SortDirection as SortDirection,
  SearchHokkaidoR8HouseholderCodeStatusesResponse,
  SearchHokkaidoR8HouseholderCodeStatusesResponse_Row,
  SearchHokkaidoR8HouseholderCodeStatusesResponse_Row_PostalReturn,
  UploadHokkaidoHouseholderCSVRequest,
  UploadHokkaidoHouseholderCSVResponse,
} from "schema/gen/es/chiikipoint/adminconsole/v2/service_pb";
import {
  HokkaidoR8Householder,
  HokkaidoR8HouseholderCodeStatus,
  User_Gender,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";

import { simulateDelay } from "./utils";

export const mockUploadHokkaidoHouseholderCSV = async (
  req: UploadHokkaidoHouseholderCSVRequest,
): Promise<UploadHokkaidoHouseholderCSVResponse> => {
  await simulateDelay();
  return new UploadHokkaidoHouseholderCSVResponse({
    objectKey: `mock/${req.filename}`,
  });
};

type MockCodeUse = "unused" | "wallet_balance" | "paper_voucher";

type MockHouseholder = {
  nameKanji: string;
  nameKana: string;
  gender: User_Gender;
  birthday: Date;
  postalCode: string;
  address: string;
  addressSupplement: string;
  use: MockCodeUse;
  usedAt?: Date;
  userId?: string;
  subscriptionId?: string;
  staffId?: string;
  // Paper voucher issuance (set when use becomes "paper_voucher").
  issuedAt?: Date;
  // Postal return (ハガキ返戻) registration.
  postalReturnReason?: string;
  postalReturnedAt?: Date;
};

const MOCK_USED_AT = new Date("2026-05-20T10:00:00+09:00");
const MOCK_ISSUED_AT = new Date("2026-05-22T14:30:00+09:00");
const MOCK_RETURNED_AT = new Date("2026-05-25T09:15:00+09:00");
const MUNICIPALITY_CODE = "010006";

// Hardcoded test householders; absent codes are CODE_NOT_FOUND. Resets on reload.
const mockHouseholders = new Map<string, MockHouseholder>([
  ["22T9A2Z4GAK7", { nameKanji: "北海道太郎", nameKana: "ホッカイドウタロウ", gender: User_Gender.MALE, birthday: new Date("1980-04-01"), postalCode: "0600001", address: "北海道札幌市中央区北一条西1-1", addressSupplement: "", use: "unused" }],
  ["234R5CKGD7GG", { nameKanji: "北海道花子", nameKana: "ホッカイドウハナコ", gender: User_Gender.FEMALE, birthday: new Date("1985-07-15"), postalCode: "0600002", address: "北海道札幌市中央区北二条西2-2", addressSupplement: "サンプルマンション101", use: "wallet_balance", usedAt: MOCK_USED_AT, userId: "mock-user-0001", subscriptionId: "SUB-MOCK-0001" }],
  ["22WPQ87SF4HF", { nameKanji: "札幌一郎", nameKana: "サッポロイチロウ", gender: User_Gender.MALE, birthday: new Date("1972-11-03"), postalCode: "0600003", address: "北海道札幌市中央区北三条西3-3", addressSupplement: "", use: "paper_voucher", usedAt: MOCK_ISSUED_AT, issuedAt: MOCK_ISSUED_AT, staffId: "mock-staff-0001" }],
  ["22VVSW64D7VF", { nameKanji: "札幌次郎", nameKana: "サッポロジロウ", gender: User_Gender.MALE, birthday: new Date("1990-02-20"), postalCode: "0600004", address: "北海道札幌市中央区北四条西4-4", addressSupplement: "", use: "paper_voucher", usedAt: MOCK_ISSUED_AT, issuedAt: MOCK_ISSUED_AT, staffId: "mock-staff-0001", postalReturnReason: "宛先不明", postalReturnedAt: MOCK_RETURNED_AT }],
  ["22Y4PYBGF5XT", { nameKanji: "旭川三郎", nameKana: "アサヒカワサブロウ", gender: User_Gender.MALE, birthday: new Date("1965-08-30"), postalCode: "0700000", address: "北海道旭川市1条通1", addressSupplement: "", use: "unused", postalReturnReason: "転居不明", postalReturnedAt: MOCK_RETURNED_AT }],
  ["235K9KYNR3MY", { nameKanji: "函館史郎", nameKana: "ハコダテシロウ", gender: User_Gender.MALE, birthday: new Date("1998-01-09"), postalCode: "0400000", address: "北海道函館市本町1", addressSupplement: "", use: "unused" }],
  ["236CQGR7TYSN", { nameKanji: "小樽五郎", nameKana: "オタルゴロウ", gender: User_Gender.MALE, birthday: new Date("1955-05-05"), postalCode: "0470000", address: "北海道小樽市色内1", addressSupplement: "", use: "unused" }],
  ["2378EWSBR4ES", { nameKanji: "釧路六郎", nameKana: "クシロロクロウ", gender: User_Gender.MALE, birthday: new Date("2000-12-25"), postalCode: "0850000", address: "北海道釧路市黒金町1", addressSupplement: "", use: "unused" }],
  ["238YX8N5TN39", { nameKanji: "帯広奈々子", nameKana: "オビヒロナナコ", gender: User_Gender.FEMALE, birthday: new Date("1988-06-18"), postalCode: "0800000", address: "北海道帯広市西1条南1", addressSupplement: "", use: "unused" }],
  ["239YJABVVTEC", { nameKanji: "苫小牧八郎", nameKana: "トマコマイハチロウ", gender: User_Gender.MALE, birthday: new Date("1979-09-14"), postalCode: "0530000", address: "北海道苫小牧市旭町1", addressSupplement: "", use: "unused" }],
]);

const Reason = CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError_Reason;

const rowError = (
  index: number,
  reason: CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError_Reason,
) =>
  new CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError({
    index,
    reason,
  });

const batchError = (
  rowErrors: CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError[],
) =>
  new ConnectError(
    "paper voucher batch validation failed",
    Code.FailedPrecondition,
    undefined,
    rowErrors,
  );

export const mockCreateHokkaidoR8HouseholderCodeUsesAsPaperVoucher = async (
  req: CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRequest,
): Promise<CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherResponse> => {
  await simulateDelay();
  const keys = req.codes;

  // Phase 1: structural checks (malformed → duplicate → not-found).
  const seen = new Set<string>();
  const structuralErrors: CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError[] =
    [];
  keys.forEach((key, index) => {
    if (key.length === 0) {
      structuralErrors.push(rowError(index, Reason.MALFORMED_CODE));
      return;
    }
    if (seen.has(key)) {
      structuralErrors.push(rowError(index, Reason.DUPLICATE_IN_REQUEST));
      return;
    }
    seen.add(key);
    if (!mockHouseholders.has(key)) {
      structuralErrors.push(rowError(index, Reason.CODE_NOT_FOUND));
    }
  });
  if (structuralErrors.length > 0) {
    throw batchError(structuralErrors);
  }

  // Phase 2: already-used checks, only after all rows pass phase 1.
  const usedErrors: CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherRowError[] =
    [];
  keys.forEach((key, index) => {
    const use = mockHouseholders.get(key)?.use;
    if (use === "wallet_balance") {
      usedErrors.push(rowError(index, Reason.ALREADY_USED_AS_WALLET_BALANCE));
    } else if (use === "paper_voucher") {
      usedErrors.push(rowError(index, Reason.ALREADY_USED_AS_PAPER_VOUCHER));
    }
  });
  if (usedErrors.length > 0) {
    throw batchError(usedErrors);
  }

  const usedAt = new Date();
  for (const key of keys) {
    // existing is guaranteed by the CODE_NOT_FOUND filter above.
    const existing = mockHouseholders.get(key)!;
    mockHouseholders.set(key, {
      ...existing,
      use: "paper_voucher",
      usedAt,
      issuedAt: usedAt,
      staffId: "mock-staff",
    });
  }
  return new CreateHokkaidoR8HouseholderCodeUsesAsPaperVoucherResponse();
};

const PostalReturnReason = CreateHokkaidoR8HouseholderPostalReturnRowError_Reason;

const postalReturnRowError = (
  index: number,
  reason: CreateHokkaidoR8HouseholderPostalReturnRowError_Reason,
) =>
  new CreateHokkaidoR8HouseholderPostalReturnRowError({
    index,
    reason,
  });

const postalReturnBatchError = (
  rowErrors: CreateHokkaidoR8HouseholderPostalReturnRowError[],
) =>
  new ConnectError(
    "postal return batch validation failed",
    Code.FailedPrecondition,
    undefined,
    rowErrors,
  );

export const mockCreateHokkaidoR8HouseholderPostalReturns = async (
  req: CreateHokkaidoR8HouseholderPostalReturnsRequest,
): Promise<CreateHokkaidoR8HouseholderPostalReturnsResponse> => {
  await simulateDelay();
  const keys = req.codes;

  // Structural checks (malformed → duplicate → not-found), mirroring the
  // paper-voucher create mock. Postal returns have no "already used" phase.
  const seen = new Set<string>();
  const structuralErrors: CreateHokkaidoR8HouseholderPostalReturnRowError[] = [];
  keys.forEach((key, index) => {
    if (key.length === 0) {
      structuralErrors.push(
        postalReturnRowError(index, PostalReturnReason.MALFORMED_CODE),
      );
      return;
    }
    if (seen.has(key)) {
      structuralErrors.push(
        postalReturnRowError(index, PostalReturnReason.DUPLICATE_IN_REQUEST),
      );
      return;
    }
    seen.add(key);
    if (!mockHouseholders.has(key)) {
      structuralErrors.push(
        postalReturnRowError(index, PostalReturnReason.CODE_NOT_FOUND),
      );
    }
  });
  if (structuralErrors.length > 0) {
    throw postalReturnBatchError(structuralErrors);
  }

  const returnedAt = new Date();
  for (const key of keys) {
    const existing = mockHouseholders.get(key)!;
    mockHouseholders.set(key, {
      ...existing,
      postalReturnReason: req.reason,
      postalReturnedAt: returnedAt,
      staffId: "mock-staff",
    });
  }
  return new CreateHokkaidoR8HouseholderPostalReturnsResponse();
};

const toHouseholder = (
  compact: string,
  h: MockHouseholder,
): HokkaidoR8Householder =>
  new HokkaidoR8Householder({
    code: toDisplayCode(compact),
    municipalityCode: MUNICIPALITY_CODE,
    municipalityName: "北海道札幌市",
    nameKanji: h.nameKanji,
    nameKana: h.nameKana,
    gender: h.gender,
    birthday: Timestamp.fromDate(h.birthday),
    postalCode: h.postalCode,
    address: h.address,
    addressSupplement: h.addressSupplement,
  });

const toDisplayCode = (compact: string) =>
  `${compact.slice(0, 3)}-${compact.slice(3, 11)}-${compact.slice(11)}`;

const mockStatusOf = (use: MockCodeUse): HokkaidoR8HouseholderCodeStatus => {
  switch (use) {
    case "wallet_balance":
      return HokkaidoR8HouseholderCodeStatus.USED_AS_WALLET_BALANCE;
    case "paper_voucher":
      return HokkaidoR8HouseholderCodeStatus.USED_AS_PAPER_VOUCHER;
    default:
      return HokkaidoR8HouseholderCodeStatus.UNUSED;
  }
};

export const mockSearchHokkaidoR8HouseholderCodeStatuses = async (
  req: SearchHokkaidoR8HouseholderCodeStatusesRequest,
): Promise<SearchHokkaidoR8HouseholderCodeStatusesResponse> => {
  await simulateDelay();

  let rows = [...mockHouseholders.entries()].map(([code, h]) => ({
    code,
    nameKana: h.nameKana,
    gender: h.gender,
    birthday: h.birthday,
    status: mockStatusOf(h.use),
    userId: h.userId,
    subscriptionId: h.subscriptionId,
    staffId: h.staffId,
    usedAt: h.usedAt,
    postalReturnReason: h.postalReturnReason,
    postalReturnedAt: h.postalReturnedAt,
  }));

  if (req.code) {
    const compact = req.code.replace(/-/g, "").toUpperCase();
    rows = rows.filter((r) => r.code === compact);
  }
  if (req.nameKana) {
    rows = rows.filter((r) => r.nameKana.includes(req.nameKana ?? ""));
  }
  if (
    req.status !== undefined &&
    req.status !== HokkaidoR8HouseholderCodeStatus.UNSPECIFIED
  ) {
    rows = rows.filter((r) => r.status === req.status);
  }
  if (req.usedAtStart) {
    const from = req.usedAtStart.toDate();
    rows = rows.filter((r) => r.usedAt !== undefined && r.usedAt >= from);
  }
  if (req.usedAtEnd) {
    const to = req.usedAtEnd.toDate();
    rows = rows.filter((r) => r.usedAt !== undefined && r.usedAt <= to);
  }

  const dir = req.sortDirection === SortDirection.DESC ? -1 : 1;
  const key = req.orderBy ?? OrderBy.USED_AT;
  rows.sort((a, b) => {
    let c = 0;
    if (key === OrderBy.CODE) {
      c = a.code.localeCompare(b.code);
    } else if (key === OrderBy.NAME_KANA) {
      c = a.nameKana.localeCompare(b.nameKana);
    } else {
      // used_at: NULLs last regardless of direction
      const at = a.usedAt?.getTime();
      const bt = b.usedAt?.getTime();
      if (at === undefined && bt === undefined) c = 0;
      else if (at === undefined) return 1;
      else if (bt === undefined) return -1;
      else c = at - bt;
    }
    if (c !== 0) return c * dir;
    return a.code.localeCompare(b.code);
  });

  const totalCount = rows.length;
  const pageSize = req.pageSize && req.pageSize > 0 ? req.pageSize : 50;
  const page = req.page && req.page > 0 ? req.page : 1;
  const start = (page - 1) * pageSize;
  const pageRows = rows.slice(start, start + pageSize);

  return new SearchHokkaidoR8HouseholderCodeStatusesResponse({
    totalCount: BigInt(totalCount),
    rows: pageRows.map(
      (r) =>
        new SearchHokkaidoR8HouseholderCodeStatusesResponse_Row({
          code: toDisplayCode(r.code),
          status: r.status,
          userId: r.userId,
          subscriptionId: r.subscriptionId,
          staffId: r.staffId,
          usedAt: r.usedAt ? Timestamp.fromDate(r.usedAt) : undefined,
          nameKana: r.nameKana,
          gender: r.gender,
          birthday: Timestamp.fromDate(r.birthday),
          postalReturns:
            r.postalReturnReason && r.postalReturnedAt
              ? [
                  new SearchHokkaidoR8HouseholderCodeStatusesResponse_Row_PostalReturn({
                    reason: r.postalReturnReason,
                    staffId: r.staffId ?? "mock-staff",
                    returnedAt: Timestamp.fromDate(r.postalReturnedAt),
                  }),
                ]
              : [],
        }),
    ),
  });
};

export const mockGetHokkaidoR8HouseholderPaperVouchersByPeriod = async (
  req: GetHokkaidoR8HouseholderPaperVouchersByPeriodRequest,
): Promise<GetHokkaidoR8HouseholderPaperVouchersByPeriodResponse> => {
  await simulateDelay();

  const from = req.issuedAtStart?.toDate();
  const to = req.issuedAtEnd?.toDate();

  const rows = [...mockHouseholders.entries()]
    .filter(([, h]) => h.issuedAt !== undefined)
    .filter(([, h]) => (from ? h.issuedAt! >= from : true))
    .filter(([, h]) => (to ? h.issuedAt! <= to : true))
    .sort((a, b) => a[1].issuedAt!.getTime() - b[1].issuedAt!.getTime())
    .map(
      ([code, h]) =>
        new GetHokkaidoR8HouseholderPaperVouchersByPeriodResponse_Row({
          householder: toHouseholder(code, h),
          issuedAt: Timestamp.fromDate(h.issuedAt!),
          staffId: h.staffId ?? "mock-staff",
        }),
    );

  return new GetHokkaidoR8HouseholderPaperVouchersByPeriodResponse({ rows });
};

export const mockGetHokkaidoR8HouseholderPostalReturnsByPeriod = async (
  req: GetHokkaidoR8HouseholderPostalReturnsByPeriodRequest,
): Promise<GetHokkaidoR8HouseholderPostalReturnsByPeriodResponse> => {
  await simulateDelay();

  const from = req.returnedAtStart?.toDate();
  const to = req.returnedAtEnd?.toDate();

  const rows = [...mockHouseholders.entries()]
    .filter(([, h]) => h.postalReturnedAt !== undefined)
    .filter(([, h]) => (from ? h.postalReturnedAt! >= from : true))
    .filter(([, h]) => (to ? h.postalReturnedAt! <= to : true))
    .sort(
      (a, b) =>
        a[1].postalReturnedAt!.getTime() - b[1].postalReturnedAt!.getTime(),
    )
    .map(
      ([code, h]) =>
        new GetHokkaidoR8HouseholderPostalReturnsByPeriodResponse_Row({
          householder: toHouseholder(code, h),
          reason: h.postalReturnReason ?? "",
          returnedAt: Timestamp.fromDate(h.postalReturnedAt!),
          staffId: h.staffId ?? "mock-staff",
        }),
    );

  return new GetHokkaidoR8HouseholderPostalReturnsByPeriodResponse({ rows });
};
